import { LiksConfirmDialog } from '../../components/liks/LiksConfirmDialog/LiksConfirmDialog';
import {
  useAssignTenantUserGroupMutation,
  useCreateTenantGroupMutation,
  useDeleteTenantGroupMutation,
  useRenameTenantGroupMutation,
  useUnassignTenantUserGroupMutation,
} from '../../features/tenant/mutations/useTenantGroupMutations';
import { useI18n } from '../../i18n/useI18n';
import { toastMutationError } from '../../query/mutationToasts';
import { toast } from '../../toast/toast';
import type { AssignedDirectoryUser } from '../../types/user';
import type { WithResourceId } from '../../types/knowledge';
import type { UserGroup } from '../../types/tenant';
import { TenantNamedResourceDialog } from './TenantNamedResourceDialog';
import type { TenantAssignableUser } from './TenantResourceUserAssignments';
import { TenantResourceUsersDialog } from './TenantResourceUsersDialog';

type NamedGroup = WithResourceId<UserGroup> & { name: string };

type TenantGroupsSectionDialogsProps = {
  isCreateOpen: boolean;
  onCloseCreate: () => void;
  renameTarget: NamedGroup | null;
  onCloseRename: () => void;
  deleteTarget: NamedGroup | null;
  onCloseDelete: () => void;
  manageTarget: NamedGroup | null;
  onCloseManage: () => void;
  directoryUsers: ReadonlyArray<TenantAssignableUser>;
  assignedUsers: ReadonlyArray<AssignedDirectoryUser>;
  isManageLoading: boolean;
  isManageError: boolean;
  pendingUserId: string | null;
  setPendingUserId: (userId: string | null) => void;
  createMutation: ReturnType<typeof useCreateTenantGroupMutation>;
  renameMutation: ReturnType<typeof useRenameTenantGroupMutation>;
  deleteMutation: ReturnType<typeof useDeleteTenantGroupMutation>;
  assignMutation: ReturnType<typeof useAssignTenantUserGroupMutation>;
  unassignMutation: ReturnType<typeof useUnassignTenantUserGroupMutation>;
};

export function TenantGroupsSectionDialogs({
  isCreateOpen,
  onCloseCreate,
  renameTarget,
  onCloseRename,
  deleteTarget,
  onCloseDelete,
  manageTarget,
  onCloseManage,
  directoryUsers,
  assignedUsers,
  isManageLoading,
  isManageError,
  pendingUserId,
  setPendingUserId,
  createMutation,
  renameMutation,
  deleteMutation,
  assignMutation,
  unassignMutation,
}: TenantGroupsSectionDialogsProps) {
  const { translate } = useI18n();

  return (
    <>
      {isCreateOpen ? (
        <TenantNamedResourceDialog
          title={translate(
            'organisationManagement.labelsAndGroups.createGroupTitle',
          )}
          nameLabel={translate(
            'organisationManagement.labelsAndGroups.nameLabel',
          )}
          namePlaceholder={translate(
            'organisationManagement.labelsAndGroups.groupNamePlaceholder',
          )}
          submitLabel={translate(
            'organisationManagement.labelsAndGroups.createGroup',
          )}
          cancelLabel={translate(
            'organisationManagement.labelsAndGroups.cancel',
          )}
          closeLabel={translate('organisationManagement.labelsAndGroups.close')}
          isSubmitting={createMutation.isPending}
          onClose={onCloseCreate}
          onSubmit={(name) => {
            createMutation.mutate(name, {
              onSuccess: () => {
                toast.success({
                  title: translate(
                    'organisationManagement.labelsAndGroups.createGroupSuccessTitle',
                  ),
                  description: '',
                });
                onCloseCreate();
              },
              onError: (error) => {
                void toastMutationError(
                  error,
                  translate(
                    'organisationManagement.labelsAndGroups.createGroupErrorTitle',
                  ),
                );
              },
            });
          }}
        />
      ) : null}

      {renameTarget ? (
        <TenantNamedResourceDialog
          title={translate(
            'organisationManagement.labelsAndGroups.renameGroupTitle',
          )}
          nameLabel={translate(
            'organisationManagement.labelsAndGroups.nameLabel',
          )}
          namePlaceholder={translate(
            'organisationManagement.labelsAndGroups.groupNamePlaceholder',
          )}
          submitLabel={translate(
            'organisationManagement.labelsAndGroups.saveRename',
          )}
          cancelLabel={translate(
            'organisationManagement.labelsAndGroups.cancel',
          )}
          closeLabel={translate('organisationManagement.labelsAndGroups.close')}
          initialName={renameTarget.name}
          isSubmitting={renameMutation.isPending}
          onClose={onCloseRename}
          onSubmit={(name) => {
            renameMutation.mutate(
              { groupId: renameTarget.id, name },
              {
                onSuccess: () => {
                  toast.success({
                    title: translate(
                      'organisationManagement.labelsAndGroups.renameGroupSuccessTitle',
                    ),
                    description: '',
                  });
                  onCloseRename();
                },
                onError: (error) => {
                  void toastMutationError(
                    error,
                    translate(
                      'organisationManagement.labelsAndGroups.renameGroupErrorTitle',
                    ),
                  );
                },
              },
            );
          }}
        />
      ) : null}

      {manageTarget ? (
        <TenantResourceUsersDialog
          title={translate(
            'organisationManagement.labelsAndGroups.manageUsersTitle',
            { name: manageTarget.name },
          )}
          closeLabel={translate('organisationManagement.labelsAndGroups.close')}
          assignedUsers={assignedUsers}
          directoryUsers={directoryUsers}
          isLoading={isManageLoading}
          isError={isManageError}
          pendingUserId={pendingUserId}
          isBusy={assignMutation.isPending || unassignMutation.isPending}
          onClose={() => {
            if (!assignMutation.isPending && !unassignMutation.isPending) {
              onCloseManage();
              setPendingUserId(null);
            }
          }}
          onAssign={(userId) => {
            setPendingUserId(userId);
            assignMutation.mutate(
              { userId, groupId: manageTarget.id },
              {
                onSuccess: () => {
                  toast.success({
                    title: translate(
                      'organisationManagement.labelsAndGroups.assignGroupSuccessTitle',
                    ),
                    description: '',
                  });
                },
                onError: (error) => {
                  void toastMutationError(
                    error,
                    translate(
                      'organisationManagement.labelsAndGroups.assignGroupErrorTitle',
                    ),
                  );
                },
                onSettled: () => setPendingUserId(null),
              },
            );
          }}
          onUnassign={(userId) => {
            setPendingUserId(userId);
            unassignMutation.mutate(
              { userId, groupId: manageTarget.id },
              {
                onSuccess: () => {
                  toast.success({
                    title: translate(
                      'organisationManagement.labelsAndGroups.unassignGroupSuccessTitle',
                    ),
                    description: '',
                  });
                },
                onError: (error) => {
                  void toastMutationError(
                    error,
                    translate(
                      'organisationManagement.labelsAndGroups.unassignGroupErrorTitle',
                    ),
                  );
                },
                onSettled: () => setPendingUserId(null),
              },
            );
          }}
        />
      ) : null}

      <LiksConfirmDialog
        isOpen={deleteTarget != null}
        title={translate(
          'organisationManagement.labelsAndGroups.deleteGroupTitle',
        )}
        message={translate(
          'organisationManagement.labelsAndGroups.deleteGroupMessage',
          { name: deleteTarget?.name ?? '' },
        )}
        confirmLabel={translate(
          'organisationManagement.labelsAndGroups.delete',
        )}
        cancelLabel={translate('organisationManagement.labelsAndGroups.cancel')}
        onCancel={() => {
          if (!deleteMutation.isPending) {
            onCloseDelete();
          }
        }}
        onConfirm={() => {
          if (!deleteTarget || deleteMutation.isPending) {
            return;
          }
          deleteMutation.mutate(deleteTarget.id, {
            onSuccess: () => {
              toast.success({
                title: translate(
                  'organisationManagement.labelsAndGroups.deleteGroupSuccessTitle',
                ),
                description: '',
              });
              onCloseDelete();
            },
            onError: (error) => {
              void toastMutationError(
                error,
                translate(
                  'organisationManagement.labelsAndGroups.deleteGroupErrorTitle',
                ),
              );
            },
          });
        }}
      />
    </>
  );
}
